<template>
  <div class="bet-history-list">
    <div class="bet-history-list__toolbar">
      <div class="bet-history-list__filters">
        <SelectBox
          class="bet-history-list__date-select"
          :default="historySelection || 'Today'"
          :options="dateOptions"
          @update="updateDateHandler"
        />
        <button
          type="button"
          class="bet-history-list__pill"
          :class="{ 'is-active': productFilter === 'all' }"
          @click="updateProductFilterHandler('all')"
        >
          {{ $t('All') }}
        </button>
        <button
          type="button"
          class="bet-history-list__pill bet-history-list__pill--icon"
          :class="{ 'is-active': productFilter === 'tai-xiu' }"
          aria-label="Tai Xiu"
          @click="updateProductFilterHandler('tai-xiu')"
        >
          <img src="@/assets/images/dice-menu.webp" alt="" />
        </button>
        <button
          type="button"
          class="bet-history-list__pill bet-history-list__pill--icon"
          :class="{ 'is-active': productFilter === 'cockfight' }"
          aria-label="Cockfight"
          @click="updateProductFilterHandler('cockfight')"
        >
          <img src="@/assets/images/cockfight-menu-icon.svg" alt="" />
        </button>
      </div>
      <span class="bet-history-list__date-label">{{ displayDate }}</span>
    </div>

    <div class="result-main-body">
      <BetHistoryTable />
      <button v-if="hasMoreTicket" class="load-more" @click="loadMoreTickets">{{ loadMoreText }}</button>
    </div>
  </div>
</template>
<script>
import SelectBox from '@/components/select-box.vue';
import BetHistoryTable from '@/components/tables/bet-history.vue';
import { mapActions, mapGetters } from 'vuex';
import { formatAmount } from '@/utils/helpers';
import { ARENA_GAME_TYPE } from '@/utils/constant';
import { isEmpty } from 'lodash';
import { isMobile } from 'mobile-device-detect';

export default {
  components: { SelectBox, BetHistoryTable },
  data() {
    return {
      dateOptions: [
        { value: 'today', label: this.$t('Today') },
        { value: 'yesterday', label: this.$t('Yesterday') },
        { value: 'this week', label: this.$t('This week') },
        { value: 'last week', label: this.$t('Last week') },
      ],
      date: '',
      productFilter: 'all',
      isMobile,
    };
  },
  mounted() {
    if (!this.historySelection) {
      this.resetTickets();
      this.resetTicketsPage();
      this.loadMoreTickets();
    }
    this.date = this.historySelection || 'today';
  },
  beforeDestroy() {
    this.setHistoryParams(null);
  },
  watch: {
    // A round changing state is when a bet of the member's can settle.
    'fight.status': 'syncHistory',
    taiXiuStatuses: 'syncHistory',
  },
  methods: {
    ...mapActions('ticket', [
      'getTicketTotals',
      'getTickets',
      'resetTickets',
      'resetTicketsPage',
      'setHistoryParams',
      'syncHistory',
    ]),
    ...mapActions('general', ['updateGeneral']),
    formatAmount,
    updateDateHandler(date) {
      this.date = date;
      this.updateGeneral({
        constrols: {
          historySelection: date,
        },
      });
      this.resetTickets();
      this.resetTicketsPage();
      this.loadMoreTickets();
    },
    updateProductFilterHandler(productFilter) {
      if (this.productFilter === productFilter) return;
      this.productFilter = productFilter;
      this.resetTickets();
      this.resetTicketsPage();
      this.loadMoreTickets();
    },
    loadMoreTickets() {
      const params = this.generateTicketsParams();
      this.setHistoryParams(params);
      this.getTicketTotals(params);
      this.getTickets(params);
    },
    formatSignedAmount(value) {
      const amount = formatAmount(Math.abs(value || 0), 2);
      if (value > 0) return `+${amount}`;
      if (value < 0) return `-${amount}`;
      return amount;
    },
    summaryClass(value) {
      if (value > 0) return 'is-positive';
      if (value < 0) return 'is-negative';
      return '';
    },
    getCorrectedMoment() {
      if (this.serverTimeDiff && Math.abs(this.serverTimeDiff) > 300000) {
        return this.$moment().add(this.serverTimeDiff, 'milliseconds');
      }
      return this.$moment();
    },
    generateTicketsParams() {
      const presetTimezone = this.isCreditUser ? 'Africa/Juba' : this.timezone;
      let dayStr = this.getCorrectedMoment().tz(presetTimezone);
      let dayStart = dayStr.startOf('day').toISOString();
      let dayEnd = dayStr.endOf('day').toISOString();
      switch (this.date) {
        case 'yesterday':
          dayStr = this.getCorrectedMoment().tz(presetTimezone).subtract(1, 'days');
          dayStart = dayStr.startOf('day').toISOString();
          dayEnd = dayStr.endOf('day').toISOString();
          break;
        case 'this week':
          dayStr = this.getCorrectedMoment().tz(presetTimezone);
          dayStart = dayStr.startOf('isoWeek').toISOString();
          dayEnd = dayStr.endOf('isoWeek').toISOString();
          break;
        case 'last week':
          dayStr = this.getCorrectedMoment().tz(presetTimezone).subtract(1, 'weeks');
          dayStart = dayStr.startOf('isoWeek').toISOString();
          dayEnd = dayStr.endOf('isoWeek').toISOString();
          break;
      }
      return {
        from_bet_date_time: dayStart,
        to_bet_date_time: dayEnd,
        game_type: this.gameType,
      };
    },
  },
  computed: {
    ...mapGetters('fight', ['fight']),
    ...mapGetters('taiXiu', ['tables']),
    taiXiuStatuses() {
      return (this.tables || []).map((t) => `${t.currentFightId || ''}:${t.fightStatus || ''}`).join('|');
    },
    ...mapGetters({
      loading: 'ticket/ticketLoading',
      ticketListing: 'ticket/ticketListing',
      ticketTotals: 'ticket/ticketTotals',
      ticketPagination: 'ticket/ticketPagination',
      isCreditUser: 'general/withCommission',
      timezone: 'general/timezone',
      historySelection: 'general/historySelection',
      serverTimeDiff: 'general/serverTimeDiff',
      member: 'general/member',
    }),
    currencySymbol() {
      return this.member.currencySymbol;
    },
    // The tickets endpoint's product filter (`game_type`); undefined = all.
    gameType() {
      if (this.productFilter === 'tai-xiu') return ARENA_GAME_TYPE.taiXiu;
      if (this.productFilter === 'cockfight') return ARENA_GAME_TYPE.cockFight;
      return undefined;
    },
    displayDate() {
      const presetTimezone = this.isCreditUser ? 'Africa/Juba' : this.timezone;
      return this.getCorrectedMoment().tz(presetTimezone).format('DD/MM/YYYY');
    },
    /*
     * Per-day totals summed over the range, as the statement does
     * (`generateStatementData`). `totalAmount` is the day's net result -- W/L
     * after ante, plus jackpot -- not turnover, which is why it can be
     * negative and is labelled Total.
     */
    summary() {
      const totals = this.ticketTotals || [];
      return totals.reduce(
        (acc, item) => ({
          total: acc.total + Number(item.totalAmount || 0),
          ante: acc.ante + Number(item.anteAmount || 0),
          wl: acc.wl + Number(item.winLossAmount || 0),
        }),
        { total: 0, ante: 0, wl: 0 }
      );
    },
    loadMoreText() {
      return this.loading ? this.$t('loading') : this.$t('Load more');
    },
    hasMoreTicket() {
      if (!this.ticketListing || isEmpty(this.ticketListing)) return false;
      const curTicketCount = this.ticketListing.length;
      const { page: curPage, pageSize: curPageSize } = this.ticketPagination;
      return curTicketCount >= curPageSize * (curPage - 1 || 1);
    },
  },
};
</script>
<style lang="scss" scoped>
.bet-history-list {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 16px 0;
    flex-shrink: 0;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  &__date-select {
    width: auto;
    min-width: 96px;
    flex-shrink: 0;

    ::v-deep .custom-select {
      width: auto;
      min-width: 96px;
      height: 32px;
      line-height: 32px;
    }

    ::v-deep .selected {
      height: 32px;
      line-height: 32px;
      // Same border and text as the header's language pill.
      border-color: #36505d;
      color: #fff;
      border-radius: 16px;
      padding: 0 28px 0 12px;
      font-size: 12px;
      background-color: transparent;

      &:after {
        border-color: #fff;
        top: 10px;
        right: 12px;
      }
    }

    // Stays a pill while open, like the language pill.
    ::v-deep .selected.open {
      border: 1px solid #36505d;
      border-radius: 16px;
    }

    // The menu is the language menu's: its panel, white labels and blue highlight.
    ::v-deep .items {
      top: calc(100% + 8px);
      left: 0;
      right: auto;
      min-width: 140px;
      border: none;
      border-radius: 8px;
      background-color: #102741;

      div {
        height: 40px;
        line-height: 40px;
        padding: 0 12px;
        font-size: 13px;
        font-weight: 500;
        color: #fff;

        &:hover,
        &.active {
          background-image: linear-gradient(
            to right,
            rgba(91, 134, 175, 0.5),
            rgba(91, 134, 175, 0.5) 43%,
            rgba(108, 110, 115, 0) 96%
          );
        }
      }
    }
  }

  &__pill {
    height: 32px;
    min-width: 32px;
    padding: 0 14px;
    border: 1px solid #6a91a6;
    border-radius: 16px;
    background: transparent;
    // Off is grey, on is gold -- the label and the icons alike.
    color: #a1bbc9;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    cursor: pointer;
    flex-shrink: 0;

    &.is-active {
      background: linear-gradient(180deg, #f4ea9f 0%, #eac35a 100%);
      border-color: #eac35a;
      color: #4a2b14;
    }

    &--icon {
      width: 32px;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      img {
        width: 18px;
        height: 18px;
        object-fit: contain;
        // The dice art is gold and the cockfight art is blue-grey; flatten
        // both to the same grey when off, and to the dark label colour on the
        // gold fill when on.
        filter: grayscale(1) brightness(0.95);
      }
    }

    &.is-active img {
      filter: brightness(0.3);
    }
  }

  &__date-label {
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 500;
    color: #b2d6ea;
    white-space: nowrap;
  }

  &__summary {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin: 10px 16px 10px;
    padding: 10px 16px;
    border-radius: 8px;
    background-color: rgba(16, 35, 58, 0.85);
    flex-shrink: 0;
  }

  &__summary-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
  }

  &__summary-label {
    font-size: 10px;
    font-weight: 600;
    color: #6a91a7;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__summary-value {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;

    &.is-positive {
      color: #44f589;
    }

    &.is-negative {
      color: #e85050;
    }
  }

  .result-main-body {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    // Its own gap under the filters: the summary card that used to give one is hidden.
    padding: 12px 0 12px;
    box-sizing: border-box;

    button.load-more {
      @include load-more;
    }
  }
}
</style>
